import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Trash2, Volume2, Square, ToggleLeft, ToggleRight, PlusCircle } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { useSpeech } from "../hooks/useSpeech";
import { getSpeechPrefs, saveSpeechPrefs } from "../hooks/useSpeech";

const AUTOREAD_KEY = "humanizeai_chat_autoread";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("chatSession") || (() => {
    const id = `sess-${Date.now()}`; localStorage.setItem("chatSession", id); return id;
  })());
  const [autoRead, setAutoRead] = useState(() => {
    try { return JSON.parse(localStorage.getItem(AUTOREAD_KEY)) ?? false; } catch { return false; }
  });
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const ref = useRef();

  const { speakText, stop, speaking } = useSpeech();
  const prefs = getSpeechPrefs();

  useEffect(() => {
    if (!open) return;
    api.get(`/chat/${sessionId}`).then(r => setMsgs(r.data.messages || [])).catch(()=>{});
  }, [open, sessionId]);

  useEffect(() => { ref.current?.scrollTo(0, ref.current.scrollHeight); }, [msgs, loading]);

  useEffect(() => {
    if (!speaking) setSpeakingIdx(null);
  }, [speaking]);

  const toggleAutoRead = () => {
    const next = !autoRead;
    setAutoRead(next);
    localStorage.setItem(AUTOREAD_KEY, JSON.stringify(next));
  };

  const playMsg = (idx, content) => {
    if (speakingIdx === idx) { stop(); setSpeakingIdx(null); return; }
    stop();
    setSpeakingIdx(idx);
    speakText(content, prefs.rate || 1, prefs.voiceName || null);
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input;
    setInput("");
    setMsgs(m => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { sessionId, message: text });
      setMsgs(data.messages);
      if (autoRead) {
        const lastMsg = data.messages[data.messages.length - 1];
        if (lastMsg?.role === "assistant") {
          const idx = data.messages.length - 1;
          stop();
          setSpeakingIdx(idx);
          speakText(lastMsg.content, prefs.rate || 1, prefs.voiceName || null);
        }
      }
    } catch (e) {
      setMsgs(m => [...m, { role: "assistant", content: "Sorry, something went wrong." }]);
    } finally { setLoading(false); }
  };

  const clear = async () => {
    stop();
    setSpeakingIdx(null);
    await api.delete(`/chat/${sessionId}`);
    setMsgs([]);
  };

  const newChat = () => {
    stop();
    setSpeakingIdx(null);
    const id = Date.now().toString();
    localStorage.setItem("chatSession", id);
    setSessionId(id);
    setMsgs([]);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center text-white glow-primary border border-white/10 transition-transform hover:scale-105"
        >
          <MessageSquare size={22}/>
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] flex flex-col glass-strong rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
            <div>
              <div className="font-semibold text-sm">HumanizeAI Assistant</div>
              <div className="text-[10px] text-zinc-400 tracking-wider uppercase">Powered by Gemini 2.5</div>
            </div>
            <div className="flex items-center gap-1">
              {/* Auto-read toggle */}
              <button
                onClick={toggleAutoRead}
                title={autoRead ? "Auto-read on" : "Auto-read off"}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${autoRead ? "text-indigo-300" : "text-zinc-500 hover:text-zinc-300"}`}
              >
                {autoRead ? <ToggleRight size={14}/> : <ToggleLeft size={14}/>}
                <span className="hidden sm:inline">Auto-read</span>
              </button>
              <button onClick={newChat} className="p-1.5 rounded hover:bg-white/10" title="New chat"><PlusCircle size={14}/></button>
              <button onClick={clear} className="p-1.5 rounded hover:bg-white/10" title="Clear history" data-testid="chat-clear-btn"><Trash2 size={14}/></button>
              <button onClick={() => { stop(); setOpen(false); }} className="p-1.5 rounded hover:bg-white/10" data-testid="chatbot-close-btn"><X size={16}/></button>
            </div>
          </div>
          <div ref={ref} className="flex-1 overflow-y-auto p-4 space-y-3 scroll-slim">
            {msgs.length === 0 && (
              <div className="text-center text-xs text-zinc-400 py-8">
                Ask anything about HumanizeAI — plans, file formats, paraphrasing rounds…
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-1.5 items-end`}>
                <div className={`max-w-[85%] text-sm px-3 py-2 rounded-2xl ${m.role === "user" ? "bg-indigo-600 text-white rounded-br-sm" : "glass rounded-bl-sm"}`}>
                  {m.content}
                </div>
                {m.role === "assistant" && (
                  <button
                    onClick={() => playMsg(i, m.content)}
                    title={speakingIdx === i ? "Stop" : "Read aloud"}
                    className={`flex-shrink-0 p-1 rounded-full transition-colors ${speakingIdx === i ? "text-indigo-400 bg-indigo-500/10" : "text-zinc-500 hover:text-zinc-300"}`}
                  >
                    {speakingIdx === i ? <Square size={11}/> : <Volume2 size={11}/>}
                  </button>
                )}
              </div>
            ))}
            {loading && <div className="text-xs text-zinc-400">Typing…</div>}
          </div>
          <div className="p-3 border-t border-white/[0.08] flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>e.key==="Enter" && send()}
              placeholder="Ask a question…"
              className="flex-1 bg-white/[0.04] rounded-lg px-3 py-2 text-sm outline-none border border-white/[0.08] focus:border-indigo-500"
            />
            <Button onClick={send} size="sm" data-testid="chatbot-send-btn" className="bg-indigo-600 hover:bg-indigo-700">
              <Send size={14}/>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
